// @file The store for magic padlet panel displayed by DashLayoutPickerPage.vue
import { trackEvent } from '@@/bits/analytics'
import { observeConnectivity } from '@@/bits/connectivity'
import device from '@@/bits/device'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { navigateTo } from '@@/bits/location'
import type { MagicTemplateFormData, MagicTemplateKey } from '@@/bits/magic_padlet_helper'
import { isExamplePrompt, MAGIC_TEMPLATES, MAGIC_TEMPLATE_FORM_CONFIGS } from '@@/bits/magic_padlet_helper'
import { poll } from '@@/bits/polling'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { UserAccountType } from '@@/enums'
import type { Activity } from '@@/pinia/ai_streaming_store'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import { useDashMakeButtonStore } from '@@/pinia/dash_make_button_store'
import { WallCreatedFrom } from '@@/pinia/dash_make_store'
import { WallApi } from '@@/surface/api/wall'
import type {
  AiMakeSuggestion,
  LibraryId,
  MagicTemplate,
  MagicWallCreationResult,
  MagicWallCreationStatusResult,
  PopoverAnchor,
} from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { FetchError, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, isRef, ref, type Ref } from 'vue'

enum MagicPadletPanelStatus {
  Initial = 'Initial',
  Creating = 'Creating',
  Errored = 'Errored',
  AtQuota = 'AtQuota',
}

export const useMagicPadletPanelStore = defineStore('magicPadletPanelStore', () => {
  const dashAccountsStore = useDashAccountsStore()
  const dashMakeButtonStore = useDashMakeButtonStore()

  /**
   * Who can see magic templates
   */
  const xMagicTemplatesSection = computed<boolean>(() => {
    if (isAppUsing('magicPadlet')) return true

    // Tenanted users
    if (dashAccountsStore.isBackpack) {
      // Backpack students should not see the magic templates section
      if (dashAccountsStore.isStudent) return false
      else return true
    }
    if (dashAccountsStore.isBriefcase) return false

    // Personal library
    if (dashAccountsStore.currentLibrary == null) {
      return [UserAccountType.PERSONAL, UserAccountType.TEACHER, UserAccountType.STAFF].includes(
        dashAccountsStore.user.account_type as UserAccountType,
      )
    }

    // Team, School and Classroom libraries
    const isActiveLibrary: boolean =
      (!dashAccountsStore.currentLibrary.isPlanCancelled &&
        !(dashAccountsStore.currentLibrary.isTrialExpired as boolean)) ||
      dashAccountsStore.currentLibrary?.membershipTier === 'gold'
    if (!isActiveLibrary) return false

    // return true for libraries
    return true
  })

  // Only show in backpack if user is a student
  const xMagicTemplatesSectionPlaceholder = computed<boolean>(() => {
    if (xMagicTemplatesSection.value) return false

    // Handle backpack logic
    const shouldShowInBackpack = dashAccountsStore.isBackpack && dashAccountsStore.isStudent
    if (shouldShowInBackpack) return true

    return false
  })

  /**
   * Magic Templates Fields
   */
  const refs = ref<Record<string, Ref<string | string[]>>>({})

  function setRefValue(key: string, value: string | string[]): void {
    refs.value[key] = ref(value)
    // Validate form whenever a ref value is updated
    validateForm()
  }

  function getRefValue(key: string): string | string[] {
    const value = refs.value[key] as string | undefined | Ref<string | string[]>

    if (!value) {
      return ''
    }
    if (isRef(value)) {
      return value.value
    }
    return value || ''
  }

  function resetRefs(): void {
    for (const key in refs.value) {
      if (key in refs.value) {
        delete refs.value[key]
      }
    }
  }

  const inputConfig = computed(() => {
    return MAGIC_TEMPLATE_FORM_CONFIGS[magicTemplateSelected.value.key].inputConfig
  })

  /**
   * Validation
   */
  const isFormValid = ref(false)

  function isFieldEmpty(field: string | string[]): boolean {
    if (Array.isArray(field)) {
      return field.length === 0
    } else {
      return field.trim().length === 0
    }
  }

  function validateForm(): void {
    let isValid = true
    for (const config of inputConfig.value) {
      const value = getRefValue(config.refKey)
      if (config.optional !== true) {
        if (isFieldEmpty(value)) {
          isValid = false
          break
        }
      }
    }
    isFormValid.value = isValid
  }

  /**
   * Magic Template Selector
   */
  const xMagicTemplateSelector = ref(false)
  const magicTemplateSelected = ref<MagicTemplate>(MAGIC_TEMPLATES.CustomBoard)
  const shouldOnlyShowCustomBoard = computed(() => {
    return dashAccountsStore.isCurrentLibraryTeam
  })
  const magicTemplatesArray = computed<MagicTemplate[]>(() => {
    if (shouldOnlyShowCustomBoard.value) {
      return [MAGIC_TEMPLATES.CustomBoard]
    }
    return Object.values(MAGIC_TEMPLATES)
  })

  const isClassActivities = computed(() => {
    return magicTemplateSelected.value.key === 'class_activities'
  })

  const sortedMagicTemplatesByNew = computed(() => {
    if (shouldOnlyShowCustomBoard.value) {
      return [MAGIC_TEMPLATES.CustomBoard]
    }

    const allTemplates = Object.values(MAGIC_TEMPLATES)

    const discussionBoard = allTemplates.filter((template) => template.key === 'discussion_board')
    const newTemplates = allTemplates.filter(
      (template) => template.isNew === true && template.key !== 'discussion_board',
    )
    const restOfTemplates = allTemplates.filter(
      (template) => !(template.isNew ?? false) && template.key !== 'discussion_board',
    )

    return [...discussionBoard, ...newTemplates, ...restOfTemplates]
  })

  function toggleMagicTemplateSelector(): void {
    xMagicTemplateSelector.value = !xMagicTemplateSelector.value
  }

  function setMagicTemplateSelected(template: MagicTemplate): void {
    resetRefs()
    magicTemplateSelected.value = template
  }

  /**
   * Magic Template Selector Anchor
   */
  const magicTemplateSelectorAnchor = ref<Partial<PopoverAnchor>>({})
  function updateMagicTemplateSelectorAnchor(anchor: Partial<PopoverAnchor>): void {
    magicTemplateSelectorAnchor.value = anchor
  }

  /**
   * Creation
   */
  const status = ref(MagicPadletPanelStatus.Initial)
  const isInitialStatus = computed(() => status.value === MagicPadletPanelStatus.Initial)
  const isCreatingMagicPadlet = computed(() => status.value === MagicPadletPanelStatus.Creating)
  const isAtQuota = computed(() => status.value === MagicPadletPanelStatus.AtQuota)
  const isExample = ref(false)
  const includeImages = ref(true)
  const isBrowserOffline = ref(false)

  function collectFormData(
    magicTemplateKey: MagicTemplateKey,
    refs: any,
    additionalData: any = {},
  ): MagicTemplateFormData[typeof magicTemplateKey] {
    const formData: MagicTemplateFormData[typeof magicTemplateKey] = {}
    for (const key in refs.value) {
      if (key === 'userPrompt') {
        isExample.value = isExamplePrompt(getRefValue(key) as string)
      }
      formData[key] = getRefValue(key) as string
    }
    return { ...formData, ...additionalData }
  }

  async function handleApiResponse(
    currentLibraryId: LibraryId | undefined,
    includeImages: boolean,
    magicTemplateKey: MagicTemplateKey,
    formData: MagicTemplateFormData[typeof magicTemplateKey],
  ): Promise<MagicWallCreationStatusResult> {
    try {
      status.value = MagicPadletPanelStatus.Creating
      const response: JsonAPIResponse<MagicWallCreationResult> = await WallApi.createMagic({
        currentLibraryId,
        isExample: isExample.value,
        includeImages,
        magicTemplateKey,
        formData,
      })

      observeConnectivity((isOnline: boolean): void => {
        isBrowserOffline.value = !isOnline
      })

      const statusLink = (response?.data as JsonAPIResource<MagicWallCreationResult>)?.attributes.url
      const { data }: JsonAPIResponse<MagicWallCreationStatusResult> = await poll({
        pollingUrl: statusLink,
        validationCallback: ({ data }) => {
          return data?.attributes?.status !== 'in_progress' || isBrowserOffline.value
        },
        options: { intervalSecs: 5, maxAttempts: 60, method: HTTPMethod.get },
      })

      const statusResult = (data as JsonAPIResource<MagicWallCreationStatusResult>)?.attributes

      if (statusResult.status !== 'success') {
        const errorMessage = `Magic wall creation failed${
          statusResult.status === 'at_quota' ? ' due to quota' : isBrowserOffline.value ? 'due to lost connection' : ''
        }`
        throw new Error(errorMessage)
      }

      // If the user has clicked the dashed make button, check if there are any actions to perform
      await dashMakeButtonStore.handleDashMakeWallCreationActions(statusResult.wall_id)

      return statusResult
    } catch (e) {
      status.value = MagicPadletPanelStatus.Errored
      throw e // Propagate error to be handled externally
    }
  }

  async function generateMagicPadlet({
    currentLibraryId,
    includeImages,
    magicTemplateKey,
  }: {
    currentLibraryId: LibraryId | undefined
    includeImages: boolean
    magicTemplateKey: MagicTemplateKey
  }): Promise<void> {
    try {
      const formData = collectFormData(magicTemplateKey, refs)
      const statusResult = await handleApiResponse(currentLibraryId, includeImages, magicTemplateKey, formData)

      logSuccess({ currentLibraryId, isExample: isExample.value, magicTemplateKey, ...formData })
      setTimeout(() => {
        safeLocalStorage.setItem('magicWallOptions', asciiSafeStringify(formData))
        navigateTo(statusResult.wall_url, { target: device.electronApp ? '_blank' : '_self' })
        if (device.electronApp) {
          closeMagicPadletPanel()
        }
      }, 10)
    } catch (e) {
      if (e instanceof FetchError) {
        captureFetchException(e, { source: 'MagicWallCreation' })
      } else {
        captureException(e)
      }
    }
  }

  function logSuccess(logData: Record<string, any>): void {
    try {
      // Formatting
      for (const key in logData) {
        if (logData[key] === undefined || logData[key] === null || logData[key] === '') {
          delete logData[key]
        }
      }
      if (logData.grades) {
        logData.grades = logData.grades.join(', ')
      }
      if (logData.magicTemplateKey !== 'custom_board') {
        delete logData.role
        delete logData.isExample
      }

      trackEvent('Magic Padlet', 'Created magic padlet', logData.magicTemplateKey, null, logData)
    } catch (e) {
      captureException(e)
    }
  }

  async function generateClassActivityPadlet({
    currentLibraryId,
    includeImages,
    magicTemplateKey,
    topic,
    activity,
  }: {
    currentLibraryId: LibraryId | undefined
    includeImages: boolean
    magicTemplateKey: MagicTemplateKey
    topic: string
    activity: Activity
  }): Promise<void> {
    try {
      const additionalData = {
        topic,
        activityName: activity.name,
        activityDescription: activity.description,
      }
      const formData = collectFormData(magicTemplateKey, refs, additionalData)
      const statusResult = await handleApiResponse(currentLibraryId, includeImages, magicTemplateKey, formData)

      logSuccess({ currentLibraryId, isExample: isExample.value, magicTemplateKey, ...formData })
      setTimeout(() => {
        safeLocalStorage.setItem('magicWallOptions', asciiSafeStringify(formData))
        navigateTo(statusResult.wall_url, { target: device.electronApp ? '_blank' : '_self' })
        if (device.electronApp) {
          closeMagicPadletPanel()
        }
      }, 10)
    } catch (e) {
      if (e instanceof FetchError) {
        captureFetchException(e, { source: 'ClassActivityMagicWallCreation' })
      } else {
        captureException(e)
      }
    }
  }

  async function generateCustomBoardFromSuggestion(
    suggestion: AiMakeSuggestion,
    currentLibraryId: LibraryId | undefined,
    includeImages: boolean,
  ): Promise<void> {
    // Merge all structured data into a comprehensive prompt for CustomWallCreator
    const subject = suggestion.padlet_config.subject
    const grades = suggestion.padlet_config.grades.join(', ')
    const lessonObjectives = suggestion.padlet_config.lesson_objectives
    const timePeriod = suggestion.padlet_config.time_period
    const additionalDetails = suggestion.padlet_config.additional_details

    let enhancedPrompt = suggestion.long_description

    // Add structured information to the prompt
    enhancedPrompt += `\n\nSubject: ${subject}`
    enhancedPrompt += `\nGrades: ${grades}`
    enhancedPrompt += `\nLearning Objectives: ${lessonObjectives}`

    if (timePeriod) {
      enhancedPrompt += `\nTime Period: ${timePeriod}`
    }

    if (additionalDetails) {
      enhancedPrompt += `\nAdditional Details: ${additionalDetails}`
    }

    // Only pass parameters that CustomWallCreator expects
    const formData = {
      role: 'teacher',
      userPrompt: enhancedPrompt,
      created_from: WallCreatedFrom.AiMakeSuggestionBoard,
    }
    const statusResult = await handleApiResponse(
      currentLibraryId,
      includeImages,
      MAGIC_TEMPLATES.CustomBoard.key,
      formData,
    )
    logSuccess({
      currentLibraryId,
      isExample: isExample.value,
      magicTemplateKey: MAGIC_TEMPLATES.CustomBoard.key,
      ...formData,
    })
    setTimeout(() => {
      safeLocalStorage.setItem('magicWallOptions', asciiSafeStringify(formData))
      navigateTo(statusResult.wall_url, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        closeMagicPadletPanel()
      }
    }, 10)
  }

  function flipImageToggle(): void {
    includeImages.value = !includeImages.value
  }

  /**
   * Panel Actions
   */
  const xMagicPadletPanel = ref(false)

  function openMagicPadletPanel(): void {
    xMagicPadletPanel.value = true
    status.value = MagicPadletPanelStatus.Initial
    isExample.value = false
  }

  function closeMagicPadletPanel(): void {
    xMagicPadletPanel.value = false
  }

  /**
   * Multiline Input Focus
   */
  const isMultilineInputFocused = ref(false)
  function setIsMultilineInputFocused(value: boolean): void {
    isMultilineInputFocused.value = value
  }

  return {
    /**
     * Who can see magic templates
     */
    xMagicTemplatesSection,
    xMagicTemplatesSectionPlaceholder,

    /**
     * Magic Templates Fields
     */
    refs,
    setRefValue,
    getRefValue,
    inputConfig,

    /**
     * Validation
     */
    isFormValid,
    validateForm,

    /**
     * Magic Template Selector
     */
    xMagicTemplateSelector,
    magicTemplateSelected,
    magicTemplatesArray,
    magicTemplateSelectorAnchor,
    toggleMagicTemplateSelector,
    updateMagicTemplateSelectorAnchor,
    setMagicTemplateSelected,
    isClassActivities,
    sortedMagicTemplatesByNew,

    /**
     * Creation
     */
    status,
    isExample,
    includeImages,
    isInitialStatus,
    isCreatingMagicPadlet,
    isAtQuota,
    isBrowserOffline,
    generateMagicPadlet,
    flipImageToggle,
    generateClassActivityPadlet,
    generateCustomBoardFromSuggestion,

    /**
     * Panel Actions
     */
    xMagicPadletPanel,
    openMagicPadletPanel,
    closeMagicPadletPanel,

    /**
     * Multiline Input Focus
     */
    isMultilineInputFocused,
    setIsMultilineInputFocused,
  }
})

export { MagicPadletPanelStatus }
